import type {RepositoryPickerRepository} from '@github-ui/issues-repository-picker'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useCallback} from 'react'

import {useIssueCreateActions, useIssueCreateMetadata} from './contexts/IssueCreateDataContext'
import type {IssueFormNode, IssueTemplateNode, RepositoryNode} from './create-issue-query-types'
import {DisplayMode} from './utils/display-mode'
import {
  instanceOfBlankIssueData,
  instanceOfIssueFormData,
  instanceOfIssueTemplateData,
  type IssueCreatePayload,
  IssueCreationKind,
} from './utils/model'
import type {IssueCreateOptionConfig} from './utils/option-config'
import type {IssueCreateValueTypes} from './utils/template-args'
import {getTemplateUrl} from './utils/urls'

type useHandleTemplateChangeProps = {
  optionConfig: IssueCreateOptionConfig
  repository: RepositoryPickerRepository | undefined
  navigate: (url: string) => void
  setDisplayMode: (mode: DisplayMode) => void
}

// RELAY-CLEANUP(useHandleTemplateChange): widens nullable fields so the still-Relay parent
// data is assignable. Remove when useHandleTemplateChange leaves Relay.
export type UseHandleTemplateChangeIssueTemplateFragment = RelayCompat<
  Pick<IssueTemplateNode, '__typename' | 'name' | 'filename' | 'body' | 'title' | 'labels' | 'assignees' | 'type'>
>

// RELAY-CLEANUP(useHandleTemplateChange): RelayCompat widens nullable fields so the still-Relay parent
// data is assignable. Remove when useHandleTemplateChange leaves Relay.
export type UseHandleTemplateChangeIssueFormFragment = RelayCompat<IssueFormNode>

// RELAY-CLEANUP(useHandleTemplateChange): RelayCompat widens nullable fields so the still-Relay parent
// data is assignable. Remove when the parent queries leave Relay.
export type UseHandleTemplateChangeFragment = RelayCompat<Pick<RepositoryNode, 'issueTemplate' | 'issueForm'>>

export const getSelectedTemplate = (data: UseHandleTemplateChangeFragment): IssueCreatePayload | undefined => {
  let fetchedTemplate = undefined
  if (data.issueTemplate) {
    const templateData = data.issueTemplate
    fetchedTemplate = {
      name: templateData.name,
      fileName: templateData.filename,
      kind: IssueCreationKind.IssueTemplate,
      data: templateData,
    }
  } else if (data.issueForm) {
    const templateData = data.issueForm
    fetchedTemplate = {
      name: templateData.name,
      fileName: templateData.filename,
      kind: IssueCreationKind.IssueForm,
      data: templateData,
    }
  }
  return fetchedTemplate
}

export const useHandleTemplateChange = ({
  optionConfig,
  repository,
  navigate,
  setDisplayMode,
}: useHandleTemplateChangeProps) => {
  const {setTemplate} = useIssueCreateMetadata()
  const {reinitTitle, reinitBody, reinitAssignees, reinitLabels, reinitProjects, reinitMilestone, reinitIssueType} =
    useIssueCreateActions()

  return useCallback(
    (selectedTemplate?: IssueCreatePayload, enforcedInitialValues?: IssueCreateValueTypes, enforceNavigate = false) => {
      setTemplate(selectedTemplate)

      let newTitle = ''
      if (enforcedInitialValues?.discussion) {
        newTitle = enforcedInitialValues.discussion.title
      } else if (enforcedInitialValues?.title) {
        newTitle = enforcedInitialValues.title || ''
      } else {
        const templateTitle = selectedTemplate?.data.title || ''
        let userInputTitle = ''
        if (enforcedInitialValues?.appendTitleToTemplate) {
          userInputTitle = ` ${enforcedInitialValues.appendTitleToTemplate}`
        }
        newTitle = [templateTitle, userInputTitle].join('').trimStart()
      }

      reinitTitle(newTitle)

      let newBody = ''
      if (enforcedInitialValues?.discussion) {
        newBody = enforcedInitialValues.discussion.formattedBody || ''
      } else if (enforcedInitialValues?.body) {
        newBody = enforcedInitialValues.body || ''
      } else if (
        selectedTemplate &&
        (instanceOfIssueTemplateData(selectedTemplate.data) || instanceOfBlankIssueData(selectedTemplate.data))
      ) {
        newBody = selectedTemplate.data.body || ''
      }
      reinitBody(newBody)

      let templateLabels = []
      if (enforcedInitialValues?.discussion) {
        templateLabels = enforcedInitialValues.discussion?.labels || []
      } else if (enforcedInitialValues?.labels) {
        templateLabels = enforcedInitialValues.labels
      } else {
        templateLabels = selectedTemplate?.data.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])) || []
      }

      reinitLabels(templateLabels)

      const templateAssignees =
        enforcedInitialValues?.assignees ??
        (selectedTemplate?.data.assignees?.edges || []).flatMap(e => (e?.node ? [e?.node] : []))
      reinitAssignees(templateAssignees)

      // Modal will be shown when user clicks "Create issue" if Copilot is in assignees

      if (enforcedInitialValues?.projects) {
        reinitProjects(enforcedInitialValues.projects)
      } else if (selectedTemplate && instanceOfIssueFormData(selectedTemplate?.data)) {
        const templateProjects = selectedTemplate.data.projects?.edges?.flatMap(e => (e?.node ? [e.node] : [])) || []
        reinitProjects(templateProjects)
      }

      if (enforcedInitialValues?.milestone) {
        reinitMilestone(enforcedInitialValues?.milestone)
      }

      let templateType: IssueTypePickerIssueType | null = enforcedInitialValues?.type ?? null
      if (
        selectedTemplate &&
        !templateType &&
        (instanceOfIssueFormData(selectedTemplate.data) || instanceOfIssueTemplateData(selectedTemplate.data)) &&
        selectedTemplate.data.type !== undefined
      ) {
        templateType = selectedTemplate.data.type || null
      }
      reinitIssueType(templateType ?? null)

      if (!enforcedInitialValues || enforceNavigate) {
        if (optionConfig.navigateToFullScreenOnTemplateChoice && repository && selectedTemplate) {
          navigate(getTemplateUrl(repository.nameWithOwner, selectedTemplate.fileName))
        } else {
          setDisplayMode(DisplayMode.IssueCreation)
        }
      }
    },
    [
      setTemplate,
      reinitTitle,
      reinitBody,
      reinitLabels,
      reinitAssignees,
      reinitIssueType,
      reinitProjects,
      reinitMilestone,
      optionConfig.navigateToFullScreenOnTemplateChoice,
      repository,
      navigate,
      setDisplayMode,
    ],
  )
}
